import React, { createContext, useContext, useEffect, useState } from 'react';
import { usePost, useToken } from '../API';
import type { Payment } from '@aidkitorg/aidkit/lib/admin_panel/payments';
import { PaymentBatchRecipient } from '../AdminPanel/Payments/components/PaymentBatchRecipientsDataTable';

const PAYMENT_BATCH_RECIPIENT_DATA: PaymentBatchRecipient[] = [
  {
    uuid: "1a2b3c4d-0001",
    firstName: "Alice",
    lastName: "Johnson",
    email: "alice.johnson@example.com",
    phone: "+1-555-123-4567",
    paymentMethod: "direct_deposit",
    paymentStatus: "pending",
    queuedDate: new Date("2024-04-01"),
    treatmentControlGroup: "treatment",
    paymentDetails: "Payment initiated via ACH transfer"
  },
  {
    uuid: "1a2b3c4d-0002",
    firstName: "Bob",
    lastName: "Smith",
    email: "bob.smith@example.com",
    phone: "+1-555-987-6543",
    paymentMethod: "ach",
    paymentStatus: "failed",
    queuedDate: new Date("2024-04-03"),
    treatmentControlGroup: "control",
    paymentDetails: "Invalid bank account number"
  },
  {
    uuid: "1a2b3c4d-0003",
    firstName: "Carla",
    lastName: "Garcia",
    email: "carla.garcia@example.com",
    phone: "+1-555-222-3333",
    paymentMethod: "",
    paymentStatus: "pending",
    queuedDate: new Date("2024-03-29"),
    treatmentControlGroup: "treatment",
    paymentDetails: "Awaiting bank verification"
  },
  {
    uuid: "1a2b3c4d-0004",
    firstName: "David",
    lastName: "Lee",
    email: "david.lee@example.com",
    phone: "+1-555-333-4444",
    paymentMethod: "ach",
    paymentStatus: "paid",
    queuedDate: new Date("2024-04-05"),
    treatmentControlGroup: "control",
    paymentDetails: "Payment initiated via ACH transfer"
  },
  {
    uuid: "1a2b3c4d-0005",
    firstName: "Ella",
    lastName: "Nguyen",
    email: "ella.nguyen@example.com",
    phone: "+1-555-555-1234",
    paymentMethod: "direct_deposit",
    paymentStatus: "paid",
    queuedDate: new Date("2024-03-31"),
    treatmentControlGroup: "treatment",
    paymentDetails: "Debit card mailed"
  },
  {
    uuid: "1a2b3c4d-0006",
    firstName: "Frank",
    lastName: "Williams",
    email: "frank.williams@example.com",
    phone: "+1-555-444-5555",
    paymentMethod: "",
    paymentStatus: "paused",
    queuedDate: new Date("2024-04-06"),
    treatmentControlGroup: "control",
    paymentDetails: ""
  },
  {
    uuid: "1a2b3c4d-0007",
    firstName: "Grace",
    lastName: "Chen",
    email: "grace.chen@example.com",
    phone: "+1-555-111-2222",
    paymentMethod: "ach",
    paymentStatus: "failed",
    queuedDate: new Date("2024-04-04"),
    treatmentControlGroup: "treatment",
    paymentDetails: "Invalid bank account number"
  },
  {
    uuid: "1a2b3c4d-0008",
    firstName: "Henry",
    lastName: "Brown",
    email: "henry.brown@example.com",
    phone: "+1-555-999-8888",
    paymentMethod: "direct_deposit",
    paymentStatus: "paid",
    queuedDate: new Date("2024-04-02"),
    treatmentControlGroup: "control",
    paymentDetails: "Payment initiated via ACH transfer"
  },
  {
    uuid: "1a2b3c4d-0009",
    firstName: "Isla",
    lastName: "Martinez",
    email: "isla.martinez@example.com",
    phone: "+1-555-666-7777",
    paymentMethod: "ach",
    paymentStatus: "paused",
    queuedDate: new Date("2024-03-30"),
    treatmentControlGroup: "treatment",
    paymentDetails: "Awaiting bank verification"
  },
  {
    uuid: "1a2b3c4d-0010",
    firstName: "Jack",
    lastName: "Davis",
    email: "jack.davis@example.com",
    phone: "+1-555-000-1111",
    paymentMethod: "",
    paymentStatus: "",
    queuedDate: new Date("2024-04-01"),
    treatmentControlGroup: "control",
    paymentDetails: ""
  },
  {
    uuid: "1a2b3c4d-0011",
    firstName: "Kara",
    lastName: "Patel",
    email: "kara.patel@example.com",
    phone: "+1-555-321-6543",
    paymentMethod: "ach",
    paymentStatus: "paid",
    queuedDate: new Date("2024-04-06"),
    treatmentControlGroup: "treatment",
    paymentDetails: "Debit card requested"
  },
  {
    uuid: "1a2b3c4d-0012",
    firstName: "Leo",
    lastName: "Wright",
    email: "leo.wright@example.com",
    phone: "+1-555-432-1234",
    paymentMethod: "direct_deposit",
    paymentStatus: "failed",
    queuedDate: new Date("2024-04-07"),
    treatmentControlGroup: "control",
    paymentDetails: "Invalid bank account number"
  },
  {
    uuid: "1a2b3c4d-0013",
    firstName: "Maya",
    lastName: "Rivera",
    email: "maya.rivera@example.com",
    phone: "+1-555-777-8888",
    paymentMethod: "",
    paymentStatus: "",
    queuedDate: new Date("2024-03-28"),
    treatmentControlGroup: "treatment",
    paymentDetails: ""
  },
  {
    uuid: "1a2b3c4d-0014",
    firstName: "Noah",
    lastName: "Kim",
    email: "noah.kim@example.com",
    phone: "+1-555-246-8100",
    paymentMethod: "ach",
    paymentStatus: "paid",
    queuedDate: new Date("2024-04-08"),
    treatmentControlGroup: "control",
    paymentDetails: "Payment initiated via ACH transfer"
  },
  {
    uuid: "1a2b3c4d-0015",
    firstName: "Olivia",
    lastName: "Young",
    email: "olivia.young@example.com",
    phone: "+1-555-135-7911",
    paymentMethod: "direct_deposit",
    paymentStatus: "paid",
    queuedDate: new Date("2024-04-09"),
    treatmentControlGroup: "treatment",
    paymentDetails: "Debit card mailed"
  },
]

type PaymentsContextType = {
  payments: Array<Payment>,
  enabledPayments: number,
  
  getRecipientDetailsByBatch: (batch: string) => Array<PaymentBatchRecipient>
}

type PaymentsProviderProps = {
  children: React.ReactNode
}

const PaymentsContext = createContext<PaymentsContextType>({
  payments: [],
  enabledPayments: 0,

  getRecipientDetailsByBatch: () => [],
})

export const usePayments = () => {
  const context = useContext(PaymentsContext)
  if (!context) {
    throw new Error("usePayments must be used within a PaymentsProvider")
  }

  return context;
}

export const PaymentsProvider: React.FC<PaymentsProviderProps> = ({ children }) => {
  const [payments, setPayments] = useState<Array<Payment>>([])
  const [enabledPayments, setEnabledPayments] = useState(0)
  const [, token] = useToken();

  const getPaymentsData = usePost("/api/admin_panel/payments")

  useEffect(() => {
    (async () => {
      const result = await getPaymentsData({})

      if (result.success && result.data) {
        setPayments(result.data.payments)
        setEnabledPayments(result.data.totalEnabledPayments)
      } else {
        // TODO: figure out how to handle errors
      }
    })()
  }, [token])

  // TODO: finalize implementation with API call
  const getRecipientDetailsByBatch = (batch: string) => {
    return PAYMENT_BATCH_RECIPIENT_DATA
  }

  const value = {
    payments,
    enabledPayments,

    getRecipientDetailsByBatch
  }

  return (
    <PaymentsContext.Provider value={value }>
      {children}
    </PaymentsContext.Provider>
  )
}
